/*
 * A.E.S. — Accessori complementari
 * File: assets/product-recommendations-custom.js
 *
 * Vanilla JS, nessuna dipendenza. Funziona con event delegation su document,
 * quindi non serve reinizializzare quando <product-recommendations> inietta
 * l'HTML in modo asincrono o quando il theme editor ricarica la sezione.
 *
 * Gestisce:
 *   1. Variant picker  -> aggiorna prezzo, immagine e disponibilita' via /products/{handle}.js
 *   2. Quantity stepper -> incremento/decremento con clamp min/max
 *   3. Checkbox        -> evidenzia la riga e aggiorna il totale
 *   4. Aggiungi al carrello multi-item -> POST /cart/add.js
 */

(function () {
  'use strict';

  var ROOT = '[data-aes-addons]';
  var ITEM = '[data-aes-item]';
  var DEFAULT_FORMAT = '\u20AC{{amount_with_comma_separator}}';

  /* ----------------------------------------------------------------
   * Utility
   * ---------------------------------------------------------------- */

  function closest(el, selector) {
    if (!el || typeof el.closest !== 'function') return null;
    return el.closest(selector);
  }

  function formatMoney(cents, format) {
    var value = Number(cents) || 0;
    var pattern = /\{\{\s*(\w+)\s*\}\}/;
    var match = String(format || DEFAULT_FORMAT).match(pattern);
    var key = match ? match[1] : 'amount';

    function build(precision, thousands, decimal) {
      var fixed = (value / 100).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    var out;
    switch (key) {
      case 'amount': out = build(2, ',', '.'); break;
      case 'amount_no_decimals': out = build(0, ',', ''); break;
      case 'amount_with_comma_separator': out = build(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = build(0, '.', ''); break;
      case 'amount_with_space_separator': out = build(2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': out = build(0, ' ', ''); break;
      case 'amount_with_apostrophe_separator': out = build(2, "'", '.'); break;
      case 'amount_with_period_and_space_separator': out = build(2, ' ', '.'); break;
      default: out = build(2, '.', ',');
    }

    return String(format || DEFAULT_FORMAT).replace(pattern, out);
  }

  function moneyFormatOf(root) {
    return (root && root.dataset.moneyFormat) || DEFAULT_FORMAT;
  }

  /* ----------------------------------------------------------------
   * Cache prodotti — una sola fetch per handle, condivisa fra righe
   * ---------------------------------------------------------------- */

  var productCache = new Map();

  function fetchProduct(root, handle) {
    if (!handle) return Promise.reject(new Error('handle mancante'));
    if (productCache.has(handle)) return productCache.get(handle);

    var base = (root.dataset.rootUrl || '/').replace(/\/$/, '');
    var request = fetch(base + '/products/' + handle + '.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .catch(function (err) {
        productCache.delete(handle);
        throw err;
      });

    productCache.set(handle, request);
    return request;
  }

  /* ----------------------------------------------------------------
   * Prezzo
   * ---------------------------------------------------------------- */

  function renderPrice(priceEl, price, compareAt, format) {
    if (!priceEl) return;

    priceEl.dataset.priceCents = String(price);

    var html = '<span class="aes-addons__price-current">' + formatMoney(price, format) + '</span>';
    if (compareAt && Number(compareAt) > Number(price)) {
      html += '<s class="aes-addons__price-was">' + formatMoney(compareAt, format) + '</s>';
    }
    priceEl.innerHTML = html;
  }

  /* ----------------------------------------------------------------
   * 1. Variant picker
   * ---------------------------------------------------------------- */

  function handleVariantChange(select) {
    var item = closest(select, ITEM);
    var root = closest(select, ROOT);
    if (!item || !root) return;

    var priceEl = item.querySelector('[data-aes-price]');
    var format = moneyFormatOf(root);
    var option = select.options[select.selectedIndex];

    // Aggiornamento immediato dai data-attribute: nessun flicker in attesa della fetch.
    if (option && option.dataset.price) {
      renderPrice(
        priceEl,
        Number(option.dataset.price),
        Number(option.dataset.compareAtPrice) || null,
        format
      );
      updateTotals(root);
    }

    // Conferma autorevole dalla Shopify product API.
    fetchProduct(root, item.dataset.productHandle)
      .then(function (product) {
        var variant = null;
        for (var i = 0; i < product.variants.length; i++) {
          if (String(product.variants[i].id) === String(select.value)) {
            variant = product.variants[i];
            break;
          }
        }
        if (!variant) return;

        renderPrice(priceEl, variant.price, variant.compare_at_price, format);
        item.classList.toggle('is-unavailable', variant.available === false);

        var checkbox = item.querySelector('[data-aes-check]');
        if (checkbox) {
          checkbox.disabled = variant.available === false;
          if (checkbox.disabled && checkbox.checked) {
            checkbox.checked = false;
            item.classList.remove('is-selected');
          }
        }

        var img = item.querySelector('.aes-addons__img');
        if (img && img.tagName === 'IMG' && variant.featured_image && variant.featured_image.src) {
          img.removeAttribute('srcset');
          img.src = variant.featured_image.src;
        }

        updateTotals(root);
      })
      .catch(function () {
        /* Il fallback dai data-attribute e' gia' stato applicato. */
      });
  }

  /* ----------------------------------------------------------------
   * 2. Quantity stepper
   * ---------------------------------------------------------------- */

  function setQuantity(input, value) {
    var min = parseInt(input.dataset.min, 10) || 1;
    var max = parseInt(input.dataset.max, 10) || 99;
    var next = Math.min(max, Math.max(min, value));

    input.value = String(next);

    var wrap = closest(input, '[data-aes-stepper]');
    if (wrap) {
      var minus = wrap.querySelector('[data-aes-step="-1"]');
      var plus = wrap.querySelector('[data-aes-step="1"]');
      if (minus) minus.disabled = next <= min;
      if (plus) plus.disabled = next >= max;
    }

    return next;
  }

  function handleStep(button) {
    var wrap = closest(button, '[data-aes-stepper]');
    if (!wrap) return;

    var input = wrap.querySelector('[data-aes-qty]');
    if (!input) return;

    var delta = Number(button.dataset.aesStep) || 0;
    setQuantity(input, (parseInt(input.value, 10) || 1) + delta);

    // Toccare la quantita' equivale a voler quel prodotto: selezioniamo la riga.
    var item = closest(wrap, ITEM);
    var checkbox = item && item.querySelector('[data-aes-check]');
    if (checkbox && !checkbox.checked && !checkbox.disabled) {
      checkbox.checked = true;
      item.classList.add('is-selected');
    }

    updateTotals(closest(wrap, ROOT));
  }

  function handleQtyInput(input) {
    var cleaned = input.value.replace(/[^0-9]/g, '');
    input.value = cleaned;
    if (cleaned === '') return;
    setQuantity(input, parseInt(cleaned, 10));
    updateTotals(closest(input, ROOT));
  }

  /* ----------------------------------------------------------------
   * 3. Checkbox
   * ---------------------------------------------------------------- */

  function handleCheck(checkbox) {
    var item = closest(checkbox, ITEM);
    if (item) item.classList.toggle('is-selected', checkbox.checked);
    updateTotals(closest(checkbox, ROOT));
  }

  /* ----------------------------------------------------------------
   * Totali e stato del pulsante
   * ---------------------------------------------------------------- */

  function collectSelection(root) {
    var selection = [];
    var items = root.querySelectorAll(ITEM);

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var checkbox = item.querySelector('[data-aes-check]');

      // Senza checkbox la riga non e' selezionabile: la saltiamo.
      if (!checkbox || !checkbox.checked || checkbox.disabled) continue;

      var variantField = item.querySelector('[data-aes-variants]');
      var qtyField = item.querySelector('[data-aes-qty]');
      var priceEl = item.querySelector('[data-aes-price]');
      var variantId = variantField ? parseInt(variantField.value, 10) : NaN;
      if (!variantId) continue;

      selection.push({
        id: variantId,
        quantity: qtyField ? (parseInt(qtyField.value, 10) || 1) : 1,
        price: priceEl ? (parseInt(priceEl.dataset.priceCents, 10) || 0) : 0
      });
    }

    return selection;
  }

  function updateTotals(root) {
    if (!root) return;

    var button = root.querySelector('[data-aes-add-selected]');
    if (!button) return;

    var label = button.querySelector('[data-aes-submit-label]');
    var selection = collectSelection(root);
    var baseLabel = button.dataset.label || 'Aggiungi al carrello';

    if (!selection.length) {
      button.disabled = true;
      if (label) label.textContent = baseLabel;
      return;
    }

    var units = 0;
    var total = 0;
    for (var i = 0; i < selection.length; i++) {
      units += selection[i].quantity;
      total += selection[i].price * selection[i].quantity;
    }

    button.disabled = false;
    if (label) {
      label.textContent =
        baseLabel +
        ' \u00B7 ' +
        units +
        (units === 1 ? ' articolo' : ' articoli') +
        ' \u00B7 ' +
        formatMoney(total, moneyFormatOf(root));
    }
  }

  /* ----------------------------------------------------------------
   * 4. Aggiunta al carrello multi-item
   * ---------------------------------------------------------------- */

  function setFeedback(root, message, tone) {
    var box = root.querySelector('[data-aes-feedback]');
    if (!box) return;
    box.textContent = message || '';
    box.classList.toggle('is-error', tone === 'error');
    box.classList.toggle('is-success', tone === 'success');
  }

  function notifyCart() {
    var detail = { source: 'aes-addons' };
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: detail }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: detail }));
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: detail })
    );
  }

  function handleAddSelected(button) {
    var root = closest(button, ROOT);
    if (!root) return;

    var selection = collectSelection(root);
    if (!selection.length) return;

    var payload = selection.map(function (entry) {
      return { id: entry.id, quantity: entry.quantity };
    });

    button.classList.add('is-busy');
    button.disabled = true;
    setFeedback(root, '', null);

    fetch(root.dataset.cartAddUrl || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: payload })
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) {
            throw new Error(data.description || data.message || 'Aggiunta non riuscita.');
          }
          return data;
        });
      })
      .then(function () {
        setFeedback(root, 'Aggiunto al carrello.', 'success');
        notifyCart();

        var items = root.querySelectorAll(ITEM);
        for (var i = 0; i < items.length; i++) {
          var checkbox = items[i].querySelector('[data-aes-check]');
          if (checkbox) checkbox.checked = false;
          items[i].classList.remove('is-selected');
        }
        updateTotals(root);

        if (root.dataset.afterAdd === 'cart') {
          window.location.href = root.dataset.cartUrl || '/cart';
        }
      })
      .catch(function (err) {
        setFeedback(root, err.message || 'Aggiunta non riuscita. Riprova.', 'error');
        updateTotals(root);
      })
      .finally(function () {
        button.classList.remove('is-busy');
      });
  }

  /* ----------------------------------------------------------------
   * Event delegation
   * ---------------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    var step = closest(event.target, '[data-aes-step]');
    if (step) {
      event.preventDefault();
      handleStep(step);
      return;
    }

    var submit = closest(event.target, '[data-aes-add-selected]');
    if (submit) {
      event.preventDefault();
      handleAddSelected(submit);
    }
  });

  document.addEventListener('change', function (event) {
    var select = closest(event.target, 'select[data-aes-variants]');
    if (select) {
      handleVariantChange(select);
      return;
    }

    var checkbox = closest(event.target, '[data-aes-check]');
    if (checkbox) handleCheck(checkbox);
  });

  document.addEventListener('input', function (event) {
    var qty = closest(event.target, '[data-aes-qty]');
    if (qty) handleQtyInput(qty);
  });

  document.addEventListener('blur', function (event) {
    var qty = closest(event.target, '[data-aes-qty]');
    if (qty && qty.value === '') setQuantity(qty, 1);
  }, true);

  document.addEventListener('keydown', function (event) {
    var qty = closest(event.target, '[data-aes-qty]');
    if (!qty) return;

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setQuantity(qty, (parseInt(qty.value, 10) || 1) + 1);
      updateTotals(closest(qty, ROOT));
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setQuantity(qty, (parseInt(qty.value, 10) || 1) - 1);
      updateTotals(closest(qty, ROOT));
    }
  });

  /* ----------------------------------------------------------------
   * Stato iniziale — anche per liste iniettate dopo il load
   * ---------------------------------------------------------------- */

  function primeAll() {
    var roots = document.querySelectorAll(ROOT);
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].dataset.aesPrimed === 'true') continue;
      roots[i].dataset.aesPrimed = 'true';
      updateTotals(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', primeAll);
  } else {
    primeAll();
  }

  document.addEventListener('shopify:section:load', primeAll);
  document.addEventListener('shopify:block:select', primeAll);

  if (typeof MutationObserver === 'function') {
    new MutationObserver(primeAll).observe(document.body, { childList: true, subtree: true });
  }
})();